import { ViewportScroller } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    AfterViewInit,
    AfterViewChecked,
} from "@angular/core";
import { Observable, Subscription, withLatestFrom, zip } from "rxjs";

import { BreadcrumbSection } from "@hermes/aphrodite/breadcrumb";
import { Asset } from "@hermes/api-model-asset";
import { ModelObject } from "@hermes/api-model-core";
import { Product, ProductTemplateType } from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";
import { BackToGridService } from "@hermes/fragments/product-utils";
import { AddToCartFacade } from "@hermes/states/add-to-cart";
import {
    ENABLE_BREADCRUMB_LVL1_CLICKABLE,
    FeatureFlagFacade,
} from "@hermes/states/flipper";
import { ModalFacade } from "@hermes/states/modal";
import { ProductPageFacade } from "@hermes/states/product-page";
import { RouterService } from "@hermes/utils-generic/services/router";

import { computeBreadcrumbSectionsForProductPage } from "./product-page.helpers";

/**
 * This is the main routed component for any product page managed by angular.
 *
 * It extract the product from the route data and gives it to components.
 *
 */
@Component({
    selector: "h-product-page",
    templateUrl: "./product-page.component.html",
    styleUrls: ["./product-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductPageComponent
    implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked
{
    /**
     * DAC Product.
     */
    public product: Product;
    public productReference: string;
    /**
     * True if user come from a grid
     */
    public displayBackButton: boolean;

    public isServer: boolean;

    /**
     * true if we active  modal is open
     */
    public zoomActive: boolean = false;

    public zoomAssets: Asset[] = [];

    public isTopLevelProduct: boolean;

    /**
     * index of selected image
     */
    public currentIndex: number = 0;

    public productTemplateType = ProductTemplateType;

    public currentAssetUrl: string;

    public breadcrumbSections: BreadcrumbSection[] = [];
    public breadcrumbTitle = $localize`:@@hermes.accessibility.breadcrumb:Product breadcrumb trail`;

    public applyProductWrapping: boolean = false;

    public isBreadcrumbLvl1Clickable$: Observable<boolean> =
        this.featureFlagFacade.isActivated(ENABLE_BREADCRUMB_LVL1_CLICKABLE);

    private subscription: Subscription = new Subscription();

    constructor(
        public readonly productPageFacade: ProductPageFacade,
        private context: Context,
        private changeDetector: ChangeDetectorRef,
        private backToGridService: BackToGridService,
        private modalFacade: ModalFacade,
        private addToCartFacade: AddToCartFacade,
        private routerService: RouterService,
        private viewportScroller: ViewportScroller,
        private featureFlagFacade: FeatureFlagFacade,
    ) {}

    public ngOnInit(): void {
        this.isServer = this.context.isInServerMode();

        this.subscription.add(
            zip(
                this.productPageFacade.currentProduct$,
                this.productPageFacade.currentProductReference$,
            )
                .pipe(withLatestFrom(this.isBreadcrumbLvl1Clickable$))
                .subscribe(([[product, productReference], isLvl1Clickable]) => {
                    this.product = ModelObject.fromJsonData(product, Product);
                    this.productReference = productReference;
                    this.breadcrumbSections =
                        computeBreadcrumbSectionsForProductPage(
                            this.product.categoriesBreadcrumbs,
                            isLvl1Clickable,
                        );
                    // TODO: deactivate after new generic template on all template types
                    if (
                        product?.templateType !== ProductTemplateType.Simple &&
                        product?.templateType !== ProductTemplateType.Beltkit &&
                        product?.templateType !==
                            ProductTemplateType.AppleWatch &&
                        product?.templateType !==
                            ProductTemplateType.DoubleFragrance &&
                        product?.templateType !== ProductTemplateType.Bikini &&
                        product?.templateType !== ProductTemplateType.Look
                    ) {
                        this.applyProductWrapping = true;
                    }
                    if (product?.templateType !== ProductTemplateType.Giftset) {
                        this.productPageFacade.pageView(this.product);
                        this.applyProductWrapping = false;
                    }
                    this.changeDetector.detectChanges();
                }),
        );

        this.subscription.add(
            // retreive zoom status, item assets and image index to display
            this.productPageFacade.zoomStatus$.subscribe((zoomModal) => {
                this.zoomActive = zoomModal.zoomStatus;
                this.currentIndex = zoomModal.currentIndex;
                this.zoomAssets = zoomModal.zoomAssets;
                this.isTopLevelProduct = zoomModal.isTopLevelProduct;
                this.currentAssetUrl = zoomModal.currentAssetUrl;
            }),
        );

        this.subscription.add(
            // retreive information about add to cart
            this.addToCartFacade.addToCartBasket$.subscribe((basket) => {
                if (basket.notifDisplayed === true) {
                    // update drupal header cart badge
                    this.updateHTMLCartBadge(basket.totalItems);
                }
            }),
        );

        if (this.context.isInBrowserMode()) {
            this.displayBackButton = this.backToGridService.hasGridUrlKey();
        }

        this.subscription.add(
            this.modalFacade.modalClosed$.subscribe(() =>
                this.addToCartFacade.closeNotification(),
            ),
        );

        this.changeDetector.detectChanges();
    }

    public ngAfterViewInit(): void {
        this.viewportScroller.scrollToPosition([0, 0]);
    }

    public ngAfterViewChecked(): void {
        if (this.product?.templateType !== ProductTemplateType.Giftset) {
            this.productPageFacade.updateProductHeadTag(
                this.product,
                this.productReference,
            );
        }
    }

    public handleGoBack(): void {
        this.productPageFacade.goBack();
        this.addToCartFacade.closeNotification();
        this.routerService.spaRedirect(
            this.backToGridService.getUrlPreviousGrid(),
        );
    }

    public redirect(url: string) {
        this.routerService.spaRedirect(url);
    }

    public ngOnDestroy(): void {
        this.modalFacade.closeModal(false);
        this.addToCartFacade.closeNotification();
        this.subscription.unsubscribe();
    }

    /**
     * Update cart badge in drupal header with given total items value
     */
    public updateHTMLCartBadge(totalItems: number): void {
        const miniCart: HTMLElement = document.querySelector(
            "#open-cart .header-count",
        );
        if (miniCart) {
            miniCart.innerHTML = `${totalItems}`;
        }
    }

    /**
     * When zoom status change: send analytics
     *
     * @param zoomStatus: boolean
     */
    public onZoomStatusChange(zoomStatus: boolean): void {
        if (!zoomStatus && this.context.isInBrowserMode()) {
            this.viewportScroller.scrollToPosition([0, 0]);
        }
        this.productPageFacade.toggleZoom({ zoomStatus });
    }
}
